/**
 * AI Inbox — Scryb-equivalent doc-to-entry pipeline.
 *
 * Layout:
 *   Left column   — recent inbox items (status pill + supplier + date).
 *   Right column  — selected item: doc preview + editable extracted fields
 *                    + Approve / Reject / Re-extract actions.
 *
 * Upload = drag-drop or click. Extraction runs synchronously on the server
 * (Phase 1a); UI shows a spinner. Once needs_review, user edits any field,
 * clicks Approve → server creates a draft bill and re-parents the file.
 */
import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "../store/auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Routes, Route, Link } from "react-router-dom";
import {
  inboxApi, bankApi,
  fmtCents,
  type ExtractedBill, type ExtractedBankStatement, type ExtractedReceipt,
  type InboxItem, type InboxKind,
} from "../lib/business";
import { coaApi } from "../lib/tenant";
import { viewFileInNewTab } from "../lib/api";

/**
 * The AI Inbox route has two shapes:
 *   /inbox        → list view (with side-by-side review on desktop)
 *   /inbox/:id    → detail view (full-page review, new browser history entry)
 *
 * On mobile, tapping a row navigates to /inbox/:id so the phone's back button
 * works properly and the user doesn't lose scroll context. On desktop, the
 * same route change also updates the URL but the list stays visible in the
 * left column — see .inbox-shell CSS.
 */
export function InboxPage() {
  return (
    <Routes>
      <Route index element={<InboxList />} />
      <Route path=":id" element={<InboxDetail />} />
    </Routes>
  );
}

function InboxList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("needs_review");

  const listQ = useQuery({
    queryKey: ["inbox", statusFilter],
    queryFn: () => inboxApi.list(statusFilter === "all" ? undefined : statusFilter),
    refetchInterval: 5000,
  });

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">AI Inbox</div>
          <h1 className="stage-title">
            Drop a bill, we'll <em>do the typing.</em>
          </h1>
          <p className="stage-sub">
            Upload a supplier bill (PDF or photo, even handwritten). We extract line items,
            supplier, dates and totals, then queue a draft bill for you to approve.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <UploadButton />
        </div>
      </div>

      <div>
        <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
          {["needs_review", "posted", "failed", "all"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className="btn btn-ghost"
              style={{
                fontSize: 11,
                padding: "4px 10px",
                background: statusFilter === s ? "var(--paper-2)" : "transparent",
                fontWeight: statusFilter === s ? 600 : 400,
              }}
            >
              {s === "needs_review" ? "To review" : s === "all" ? "All" : s.replace("_", " ")}
            </button>
          ))}
        </div>
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {listQ.isLoading && <div style={{ padding: 14, fontSize: 13, color: "var(--ink-3)" }}>Loading…</div>}
          {!listQ.isLoading && (listQ.data?.items ?? []).length === 0 && (
            <div style={{ padding: 20, fontSize: 12, color: "var(--ink-3)", fontStyle: "italic" }}>
              No items {statusFilter !== "all" ? `with status "${statusFilter.replace("_", " ")}"` : "yet"}.
              Upload a bill to get started.
            </div>
          )}
          {(listQ.data?.items ?? []).map((it) => (
            <InboxRow
              key={it.id}
              item={it}
              selected={false}
              onClick={() => navigate(`/inbox/${it.id}`)}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function InboxDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <>
      <div className="stage-header" style={{ alignItems: "center" }}>
        <div>
          <Link
            to="/inbox"
            className="btn btn-ghost"
            style={{ padding: "4px 10px", fontSize: 12, marginBottom: 6, display: "inline-block" }}
          >
            ← Back to inbox
          </Link>
          <div className="stage-eyebrow">AI Inbox · Review</div>
        </div>
      </div>
      <ReviewPane inboxId={id} onCleared={() => navigate("/inbox")} />
    </>
  );
}

function InboxRow({ item, selected, onClick }: {
  item: InboxItem;
  selected: boolean;
  onClick: () => void;
}) {
  // Different summary per source kind. Extracted shape is a union — the
  // safest way to read is to narrow on kind first.
  const j = item.extracted_json;
  let title = item.status === "extracting" ? "Extracting…" : "—";
  let subtitle = "";
  let amount = "";
  const currency = (j as ExtractedBill | ExtractedReceipt | ExtractedBankStatement)?.currency ?? "LKR";
  if (item.source_kind === "bill" && j) {
    const b = j as ExtractedBill;
    title = b.supplier?.name ?? title;
    subtitle = b.invoice_number ?? "";
    amount = b.total_cents ? fmtCents(b.total_cents, currency) : "";
  } else if (item.source_kind === "bank_statement" && j) {
    const bs = j as ExtractedBankStatement;
    title = bs.bank_name ?? title;
    subtitle = `${bs.transactions?.length ?? 0} txns${bs.period_start ? ` · ${bs.period_start}` : ""}`;
    amount = bs.closing_balance_cents ? fmtCents(bs.closing_balance_cents, currency) : "";
  } else if (item.source_kind === "receipt" && j) {
    const r = j as ExtractedReceipt;
    title = r.merchant?.name ?? title;
    subtitle = r.category_hint ?? "";
    amount = r.total_cents ? fmtCents(r.total_cents, currency) : "";
  }
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "12px 14px",
        borderTop: "0.5px solid var(--line)",
        background: selected ? "var(--paper-2)" : "transparent",
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-1)" }}>{title}</span>
        <StatusPill status={item.status} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: "var(--ink-4)" }}>
        <span>
          <KindTag kind={item.source_kind} />
          {subtitle && <> · {subtitle}</>}
        </span>
        <span className="num">{amount}</span>
      </div>
      <div style={{ fontSize: 10, color: "var(--ink-4)", marginTop: 2 }}>
        {new Date(item.created_at).toLocaleString()}
      </div>
    </button>
  );
}

function KindTag({ kind }: { kind: InboxItem["source_kind"] }) {
  const label = kind === "bank_statement" ? "Statement" : kind === "receipt" ? "Receipt" : "Bill";
  return (
    <span style={{
      fontSize: 9, padding: "1px 6px", borderRadius: 3,
      background: "var(--paper-2)", color: "var(--ink-3)", marginRight: 4,
    }}>{label}</span>
  );
}

function StatusPill({ status }: { status: InboxItem["status"] }) {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    extracting:    { bg: "var(--gold-soft, #FFF3D9)",   fg: "#6E5320", label: "Extracting…" },
    needs_review:  { bg: "var(--accent-soft, #D9F0E4)", fg: "var(--accent-deep, #285540)", label: "Review" },
    posted:        { bg: "var(--accent, #62A281)",      fg: "white", label: "Posted" },
    rejected:      { bg: "var(--paper-3, #EDE7D5)",     fg: "var(--ink-4, #7A6F4F)", label: "Rejected" },
    failed:        { bg: "var(--rose-soft, #FADCDC)",   fg: "var(--rose, #B23A1F)", label: "Failed" },
    pending:       { bg: "var(--paper-2, #F5EFE0)",     fg: "var(--ink-3, #4A4028)", label: "Pending" },
  };
  const s = map[status] ?? map.pending;
  return (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 100,
      background: s.bg, color: s.fg, fontWeight: 500, letterSpacing: "0.02em",
    }}>{s.label}</span>
  );
}

function UploadButton() {
  const qc = useQueryClient();
  // Two file inputs: one for file-picker (PDF or gallery image), one that
  // forces the OS camera via `capture="environment"`. Mobile browsers open
  // the rear camera directly on the second — desktop browsers ignore the
  // attribute and fall back to a file picker, so it's safe cross-platform.
  const fileRef = useRef<HTMLInputElement | null>(null);
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const [kind, setKind] = useState<InboxKind>("bill");
  const uploadM = useMutation({
    mutationFn: (file: File) => inboxApi.upload(file, kind),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inbox"] }),
  });
  const kindLabel: Record<InboxKind, string> = {
    bill: "Bill",
    bank_statement: "Bank statement",
    receipt: "Receipt",
  };

  return (
    <div className="inbox-upload" style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <select
        value={kind}
        onChange={(e) => setKind(e.target.value as InboxKind)}
        style={{
          padding: "8px 10px",
          fontSize: 12,
          border: "0.5px solid var(--line)",
          borderRadius: 4,
          background: "white",
        }}
      >
        <option value="bill">Supplier bill</option>
        <option value="bank_statement">Bank statement</option>
        <option value="receipt">Receipt / petty cash</option>
      </select>
      <button
        type="button"
        className="btn btn-line inbox-camera-btn"
        title="Take a photo with your camera"
        disabled={uploadM.isPending}
        onClick={() => cameraRef.current?.click()}
      >
        📷 Take photo
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={uploadM.isPending}
        onClick={() => fileRef.current?.click()}
      >
        {uploadM.isPending ? "Extracting…" : `+ Upload ${kindLabel[kind].toLowerCase()}`}
      </button>
      <input
        ref={fileRef}
        type="file"
        style={{ display: "none" }}
        accept="application/pdf,image/*"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) uploadM.mutate(f);
          if (fileRef.current) fileRef.current.value = "";
        }}
      />
      <input
        ref={cameraRef}
        type="file"
        style={{ display: "none" }}
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) uploadM.mutate(f);
          if (cameraRef.current) cameraRef.current.value = "";
        }}
      />
      {uploadM.isError && (
        <span style={{ fontSize: 11, color: "var(--rose, #B23A1F)", marginLeft: 8 }}>
          {(uploadM.error as { detail?: string; message?: string }).detail || (uploadM.error as Error).message}
        </span>
      )}
    </div>
  );
}

// ─── Review pane ─────────────────────────────────────────────────────
function ReviewPane({ inboxId, onCleared }: { inboxId: string; onCleared: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const detailQ = useQuery({
    queryKey: ["inbox", "detail", inboxId],
    queryFn: () => inboxApi.get(inboxId),
    refetchInterval: (q) => {
      const d = q.state.data;
      return d && d.status === "extracting" ? 2000 : false;
    },
  });
  // Draft holds user edits; shape depends on the item's kind. The approve
  // endpoint accepts a partial merged over the AI output.
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);

  const approveM = useMutation({
    mutationFn: () => inboxApi.approve(inboxId, draft ?? {}),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["inbox"] });
      if (res.bill) navigate(`/bills/${res.bill.id}`);
      else if (res.bank_statement) navigate("/bank");
      // receipts: stay on the inbox showing "posted" state.
    },
  });
  const rejectM = useMutation({
    mutationFn: () => inboxApi.reject(inboxId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["inbox"] }); onCleared(); },
  });
  const reextractM = useMutation({
    mutationFn: () => inboxApi.reextract(inboxId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inbox"] }),
  });

  if (detailQ.isLoading || !detailQ.data) {
    return <div className="card" style={{ padding: 20, fontSize: 13, color: "var(--ink-3)" }}>Loading…</div>;
  }

  const item = detailQ.data;
  // Merged copy of AI extraction + user edits. Cast to the right shape per kind.
  const mergedAny: Record<string, unknown> = { ...(item.extracted_json as unknown as Record<string, unknown>), ...(draft ?? {}) };
  // Shared display-level projections used by the preview column.
  const overallConfidence = (mergedAny.overall_confidence as number) ?? 0;
  const handwritten = mergedAny.handwritten === true;
  const documentLanguage = (mergedAny.document_language as string | null) ?? null;

  return (
    <div className="inbox-review">
      {/* ── Preview column ──
          Desktop: sticky-preview 500px tall.
          Mobile:  compact thumbnail strip (120px) so the extracted-fields
                   column stays above the fold. Tap the thumb to open the
                   full image in a new tab. */}
      <div className="card inbox-preview-card">
        <div style={{ padding: "10px 14px", borderBottom: "0.5px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: "var(--ink-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
            {item.attachment?.filename ?? "Document"}
          </span>
          {item.attachment && (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: 11, padding: "2px 8px", flexShrink: 0 }}
              onClick={() => viewFileInNewTab(item.attachment!.download_url)}
            >
              Open in new tab
            </button>
          )}
        </div>
        {item.attachment && item.attachment.mime_type.startsWith("image/") ? (
          <AuthImage
            url={item.attachment.download_url}
            alt={item.attachment.filename}
            onOpen={() => viewFileInNewTab(item.attachment!.download_url)}
          />
        ) : item.attachment && item.attachment.mime_type === "application/pdf" ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--ink-3)", fontSize: 12 }}>
            PDF preview isn't inlined here — click <em>Open in new tab</em> to review side-by-side.
          </div>
        ) : (
          <div style={{ padding: 40, fontSize: 12, color: "var(--ink-3)" }}>No preview available.</div>
        )}
        <div style={{ padding: "8px 14px", borderTop: "0.5px solid var(--line)", fontSize: 11, color: "var(--ink-4)" }}>
          Overall confidence: <ConfidenceBadge value={overallConfidence} />
          {handwritten && <span style={{ marginLeft: 8 }}>· Handwritten</span>}
          {documentLanguage && <span style={{ marginLeft: 8 }}>· {documentLanguage.toUpperCase()}</span>}
        </div>
      </div>

      {/* ── Fields column ── */}
      <div className="card" style={{ padding: 16 }}>
        {item.status === "extracting" && (
          <div style={{ padding: 20, textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>
            Extracting… (Claude is reading the document)
          </div>
        )}
        {item.status === "failed" && (
          <div style={{ padding: 12, background: "var(--rose-soft)", color: "var(--rose)", borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
            <strong>Extraction failed.</strong> {item.extract_error}
            <div style={{ marginTop: 8 }}>
              <button className="btn btn-line" style={{ fontSize: 11 }} disabled={reextractM.isPending} onClick={() => reextractM.mutate()}>
                {reextractM.isPending ? "Re-extracting…" : "Try again"}
              </button>
            </div>
          </div>
        )}
        {item.status === "posted" && (
          <div style={{ padding: 12, background: "var(--accent-soft)", color: "var(--accent-deep)", borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
            ✓ Posted as {item.posted_entity_type === "bill" ? "draft bill"
              : item.posted_entity_type === "bank_statement" ? "bank statement"
              : item.posted_entity_type === "payment" ? "cash payment"
              : "entry"}.
            {item.posted_entity_id && item.posted_entity_type === "bill" && (
              <button className="btn btn-ghost" style={{ fontSize: 11, marginLeft: 8 }} onClick={() => navigate(`/bills/${item.posted_entity_id}`)}>Open bill →</button>
            )}
            {item.posted_entity_type === "bank_statement" && (
              <button className="btn btn-ghost" style={{ fontSize: 11, marginLeft: 8 }} onClick={() => navigate("/bank")}>Open bank →</button>
            )}
          </div>
        )}

        {(item.status === "needs_review" || item.status === "posted") && item.extracted_json && (
          <>
            {item.source_kind === "bill" && (
              <BillFields item={item} merged={mergedAny as unknown as ExtractedBill} setDraft={setDraft} />
            )}
            {item.source_kind === "bank_statement" && (
              <BankStatementFields item={item} merged={mergedAny as unknown as ExtractedBankStatement} setDraft={setDraft} />
            )}
            {item.source_kind === "receipt" && (
              <ReceiptFields item={item} merged={mergedAny as unknown as ExtractedReceipt} setDraft={setDraft} />
            )}

            {item.status === "needs_review" && (
              <div style={{ display: "flex", gap: 8, marginTop: 20, borderTop: "0.5px solid var(--line)", paddingTop: 16 }}>
                <button
                  className="btn btn-primary"
                  onClick={() => approveM.mutate()}
                  disabled={approveM.isPending}
                >
                  {approveM.isPending
                    ? "Posting…"
                    : item.source_kind === "bill"
                      ? "Approve → create draft bill"
                      : item.source_kind === "bank_statement"
                        ? "Approve → import statement"
                        : "Approve → post cash expense"}
                </button>
                <button className="btn btn-ghost" onClick={() => rejectM.mutate()} disabled={rejectM.isPending}>Reject</button>
                <button
                  className="btn btn-ghost"
                  style={{ marginLeft: "auto" }}
                  onClick={() => reextractM.mutate()}
                  disabled={reextractM.isPending}
                >
                  Re-extract
                </button>
              </div>
            )}
            {approveM.isError && (
              <div style={{ marginTop: 8, fontSize: 12, color: "var(--rose)" }}>
                {(approveM.error as { detail?: string; message?: string }).detail || (approveM.error as Error).message}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Kind-specific field renderers ────────────────────────────────────
type Setter = (updater: (d: Record<string, unknown> | null) => Record<string, unknown>) => void;

function BillFields({ item, merged, setDraft }: {
  item: InboxItem;
  merged: ExtractedBill;
  setDraft: Setter;
}) {
  const readOnly = item.status === "posted";
  return (
    <>
      <SectionTitle>Supplier</SectionTitle>
      <Row label="Name" value={merged.supplier?.name ?? ""} confidence={merged.supplier?._confidence}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), supplier: { ...merged.supplier, name: v } }))}
        readOnly={readOnly} />
      <Row label="VAT #" value={merged.supplier?.vat_no ?? ""}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), supplier: { ...merged.supplier, vat_no: v } }))}
        readOnly={readOnly} />
      <Row label="TIN" value={merged.supplier?.tin ?? ""}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), supplier: { ...merged.supplier, tin: v } }))}
        readOnly={readOnly} />

      <SectionTitle>Invoice</SectionTitle>
      <Row label="Invoice #" value={merged.invoice_number ?? ""}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), invoice_number: v }))}
        readOnly={readOnly} />
      <Row label="Issue date" value={merged.issue_date ?? ""} type="date"
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), issue_date: v }))}
        readOnly={readOnly} />
      <Row label="Due date" value={merged.due_date ?? ""} type="date"
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), due_date: v }))}
        readOnly={readOnly} />
      <Row label="Currency" value={merged.currency ?? "LKR"}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), currency: v }))}
        readOnly={readOnly} />

      <SectionTitle>Lines</SectionTitle>
      {(merged.lines ?? []).map((l, i) => {
        // Handwritten bills sometimes give us null for numeric fields — the
        // model marks "not sure" instead of guessing. Skip the corresponding
        // display bit rather than crashing (fmtCents(null) throws in
        // BigInt(null)).
        const cur = merged.currency ?? "LKR";
        return (
          <div key={i} style={{ padding: "8px 0", borderTop: "0.5px solid var(--line)" }}>
            <div style={{ fontSize: 13, color: "var(--ink-1)" }}>{l.description}</div>
            <div style={{ display: "flex", gap: 12, fontSize: 11, color: "var(--ink-4)", marginTop: 4, flexWrap: "wrap" }}>
              {l.quantity != null && <span>Qty: {l.quantity}</span>}
              {l.unit_price_cents && <span className="num">Unit: {fmtCents(l.unit_price_cents, cur)}</span>}
              {l.line_subtotal_cents && <span className="num">Sub: {fmtCents(l.line_subtotal_cents, cur)}</span>}
              {l.vat_amount_cents && <span className="num">VAT: {fmtCents(l.vat_amount_cents, cur)}</span>}
              <ConfidenceBadge value={l._confidence ?? 0} />
            </div>
          </div>
        );
      })}

      <SectionTitle>Totals</SectionTitle>
      <Row label="Subtotal" value={merged.subtotal_cents ?? ""} readOnly={readOnly} currency={merged.currency ?? "LKR"} />
      <Row label="VAT total" value={merged.vat_total_cents ?? ""} readOnly={readOnly} currency={merged.currency ?? "LKR"} />
      <Row label="WHT total" value={merged.wht_total_cents ?? ""} readOnly={readOnly} currency={merged.currency ?? "LKR"} />
      <Row label="Total" value={merged.total_cents ?? ""} readOnly={readOnly} currency={merged.currency ?? "LKR"} bold />
    </>
  );
}

function BankStatementFields({ item, merged, setDraft }: {
  item: InboxItem;
  merged: ExtractedBankStatement;
  setDraft: Setter;
}) {
  const banksQ = useQuery({ queryKey: ["bank-accounts"], queryFn: () => bankApi.listAccounts() });
  const readOnly = item.status === "posted";
  const currency = merged.currency ?? "LKR";
  const totalIn = (merged.transactions ?? []).filter(t => BigInt(t.amount_cents) > 0n).reduce((s, t) => s + BigInt(t.amount_cents), 0n);
  const totalOut = (merged.transactions ?? []).filter(t => BigInt(t.amount_cents) < 0n).reduce((s, t) => s + BigInt(t.amount_cents), 0n);

  return (
    <>
      <SectionTitle>Statement</SectionTitle>
      <Row label="Bank" value={merged.bank_name ?? ""} readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), bank_name: v }))} />
      <Row label="Account" value={merged.account_number ?? ""} readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), account_number: v }))} />
      <Row label="Holder" value={merged.account_holder ?? ""} readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), account_holder: v }))} />
      <Row label="Period start" value={merged.period_start ?? ""} type="date" readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), period_start: v }))} />
      <Row label="Period end" value={merged.period_end ?? ""} type="date" readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), period_end: v }))} />
      <Row label="Currency" value={merged.currency ?? "LKR"} readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), currency: v }))} />
      <Row label="Opening bal." value={merged.opening_balance_cents ?? ""} readOnly={readOnly} currency={merged.currency ?? "LKR"} />
      <Row label="Closing bal." value={merged.closing_balance_cents ?? ""} readOnly={readOnly} currency={merged.currency ?? "LKR"} bold />

      <SectionTitle>Post to bank account</SectionTitle>
      <div style={{ padding: "6px 0" }}>
        <select
          disabled={readOnly}
          onChange={(e) => setDraft((d) => ({ ...(d ?? {}), bankAccountId: e.target.value }))}
          style={{
            width: "100%", padding: "6px 8px", border: "0.5px solid var(--line)",
            borderRadius: 4, fontSize: 13, background: readOnly ? "var(--paper-2)" : "white",
          }}
        >
          <option value="">— pick a bank account —</option>
          {(banksQ.data?.accounts ?? []).map((b) => (
            <option key={b.id} value={b.id}>{b.accountName} · {b.accountNumber}</option>
          ))}
        </select>
      </div>

      <SectionTitle>Transactions ({merged.transactions?.length ?? 0})</SectionTitle>
      <div style={{ display: "flex", gap: 16, fontSize: 11, color: "var(--ink-4)", marginBottom: 8 }}>
        <span className="num">In: {fmtCents(totalIn.toString(), currency)}</span>
        <span className="num">Out: {fmtCents(totalOut.toString(), currency)}</span>
        <span className="num">Net: {fmtCents((totalIn + totalOut).toString(), currency)}</span>
      </div>
      <div style={{ maxHeight: 400, overflowY: "auto", border: "0.5px solid var(--line)", borderRadius: 4 }}>
        {(merged.transactions ?? []).map((t, i) => {
          const isDebit = BigInt(t.amount_cents) < 0n;
          return (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "80px 1fr 100px 30px",
              gap: 8, padding: "6px 8px", fontSize: 11, borderTop: i > 0 ? "0.5px solid var(--line)" : "none",
            }}>
              <span style={{ color: "var(--ink-4)" }}>{t.date}</span>
              <span style={{ color: "var(--ink-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.description}</span>
              <span className="num" style={{ textAlign: "right", color: isDebit ? "var(--rose)" : "var(--accent-deep)" }}>
                {fmtCents(t.amount_cents, currency)}
              </span>
              <ConfidenceBadge value={t._confidence} />
            </div>
          );
        })}
      </div>
    </>
  );
}

function ReceiptFields({ item, merged, setDraft }: {
  item: InboxItem;
  merged: ExtractedReceipt;
  setDraft: Setter;
}) {
  const banksQ = useQuery({ queryKey: ["bank-accounts"], queryFn: () => bankApi.listAccounts() });
  const coaQ = useQuery({ queryKey: ["coa"], queryFn: () => coaApi.mine() });
  const expenseAccounts = (coaQ.data?.accounts ?? []).filter((a) => a.type === "expense");
  const readOnly = item.status === "posted";
  return (
    <>
      <SectionTitle>Merchant</SectionTitle>
      <Row label="Name" value={merged.merchant?.name ?? ""} confidence={merged.merchant?._confidence}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), merchant: { ...merged.merchant, name: v } }))}
        readOnly={readOnly} />
      <Row label="Address" value={merged.merchant?.address ?? ""} readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), merchant: { ...merged.merchant, address: v } }))} />

      <SectionTitle>Receipt</SectionTitle>
      <Row label="Date" value={merged.receipt_date ?? ""} type="date" readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), receipt_date: v }))} />
      <Row label="Currency" value={merged.currency ?? "LKR"} readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), currency: v }))} />
      <Row label="Total" value={merged.total_cents ?? ""} currency={merged.currency ?? "LKR"} bold readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), total_cents: v }))} />
      <Row label="VAT" value={merged.vat_amount_cents ?? ""} currency={merged.currency ?? "LKR"} readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), vat_amount_cents: v }))} />
      <Row label="Payment" value={merged.payment_method ?? ""} readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), payment_method: v }))} />
      <Row label="Description" value={merged.description ?? ""} readOnly={readOnly}
        onChange={(v) => setDraft((d) => ({ ...(d ?? {}), description: v }))} />

      <SectionTitle>Post to</SectionTitle>
      <div style={{ padding: "6px 0" }}>
        <label style={{ display: "block", fontSize: 12, color: "var(--ink-3)", marginBottom: 4 }}>
          Expense account (required)
          {merged.category_hint && <span style={{ marginLeft: 6, fontSize: 10, color: "var(--ink-4)" }}>AI hint: {merged.category_hint}</span>}
        </label>
        <select
          disabled={readOnly}
          onChange={(e) => setDraft((d) => ({ ...(d ?? {}), expenseAccountId: e.target.value }))}
          style={{
            width: "100%", padding: "6px 8px", border: "0.5px solid var(--line)",
            borderRadius: 4, fontSize: 13, background: readOnly ? "var(--paper-2)" : "white",
          }}
        >
          <option value="">— pick an expense account —</option>
          {expenseAccounts.map((a) => (
            <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
          ))}
        </select>
      </div>
      <div style={{ padding: "6px 0" }}>
        <label style={{ display: "block", fontSize: 12, color: "var(--ink-3)", marginBottom: 4 }}>
          Paid from (optional — leave blank for petty cash)
        </label>
        <select
          disabled={readOnly}
          onChange={(e) => setDraft((d) => ({ ...(d ?? {}), bankAccountId: e.target.value || undefined }))}
          style={{
            width: "100%", padding: "6px 8px", border: "0.5px solid var(--line)",
            borderRadius: 4, fontSize: 13, background: readOnly ? "var(--paper-2)" : "white",
          }}
        >
          <option value="">— Petty cash —</option>
          {(banksQ.data?.accounts ?? []).map((b) => (
            <option key={b.id} value={b.id}>{b.accountName} · {b.accountNumber}</option>
          ))}
        </select>
      </div>
    </>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
      color: "var(--ink-4)", marginTop: 16, marginBottom: 6,
    }}>{children}</div>
  );
}

/**
 * A single label + input pair used across the review UI.
 *
 * Money fields pass `currency`; the row then displays the value as
 * "LKR 2,911.56" instead of the raw "291156" cents string. When editable,
 * the user types rupees (e.g. "2911.56" or even "2,911.56") and we convert
 * back to cents before calling onChange, so the parent's state (which
 * mirrors what gets POSTed to /approve) stays in cents.
 */
function Row({ label, value, onChange, confidence, type, readOnly, num, bold, currency }: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  confidence?: number;
  type?: string;
  readOnly?: boolean;
  num?: boolean;
  bold?: boolean;
  currency?: string;    // when set, `value` is cents-string; display as rupees
}) {
  const isMoney = !!currency;
  const displayValue = isMoney && value
    ? fmtCents(value, currency).replace(/^\D+\s*/, "")   // strip "LKR " prefix from the input
    : value;
  const handleChange = (raw: string) => {
    if (!onChange) return;
    if (!isMoney) { onChange(raw); return; }
    // User typed a rupee amount (possibly with commas, possibly with a
    // decimal). Convert back to cents (BigInt-safe string).
    const cleaned = raw.replace(/,/g, "").trim();
    if (!cleaned) { onChange(""); return; }
    const parts = cleaned.split(".");
    const rupees = parts[0] || "0";
    const cents = (parts[1] ?? "").padEnd(2, "0").slice(0, 2);
    // Reject anything that isn't digits (silently — keep last valid state).
    if (!/^\d+$/.test(rupees) || (parts[1] !== undefined && !/^\d+$/.test(cents))) return;
    onChange(`${BigInt(rupees) * 100n + BigInt(cents)}`);
  };

  return (
    <div className="inbox-field-row">
      <div style={{ fontSize: 12, color: "var(--ink-3)" }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {isMoney && (
          <span style={{ fontSize: 11, color: "var(--ink-4)" }}>{currency}</span>
        )}
        <input
          type={type ?? "text"}
          inputMode={isMoney ? "decimal" : undefined}
          value={displayValue}
          onChange={(e) => handleChange(e.target.value)}
          readOnly={readOnly}
          className={num || isMoney ? "num" : ""}
          style={{
            flex: 1,
            padding: "6px 8px",
            border: "0.5px solid var(--line)",
            borderRadius: 4,
            fontSize: 13,
            fontWeight: bold ? 600 : 400,
            background: readOnly ? "var(--paper-2)" : "white",
            textAlign: isMoney ? "right" : "left",
          }}
        />
        {confidence !== undefined && <ConfidenceBadge value={confidence} />}
      </div>
    </div>
  );
}

function ConfidenceBadge({ value }: { value: number }) {
  const pct = Math.round((value ?? 0) * 100);
  const bg = pct >= 90 ? "var(--accent-soft, #D9F0E4)"
    : pct >= 60 ? "var(--gold-soft, #FFF3D9)"
    : "var(--rose-soft, #FADCDC)";
  const fg = pct >= 90 ? "var(--accent-deep, #285540)"
    : pct >= 60 ? "#6E5320"
    : "var(--rose, #B23A1F)";
  return (
    <span style={{
      fontSize: 10, padding: "2px 6px", borderRadius: 100,
      background: bg, color: fg, fontWeight: 500,
    }}>{pct}%</span>
  );
}

function AuthImage({ url, alt, onOpen }: { url: string; alt: string; onOpen?: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    void (async () => {
      try {
        // The backend serialiser emits download_url like "/v1/attachments/.../download".
        // In prod, that maps to nginx's /clearbooks/api/… (nginx strips /clearbooks/api
        // and prepends /v1). In dev, it stays /v1 via the Vite proxy. Either way we
        // need the ORIGINAL /v1/... URL fetched through the same BASE the rest of the
        // API client uses.
        const apiBase = import.meta.env.PROD ? "/clearbooks/api" : "";
        // Strip the /v1 prefix in prod (nginx adds it back); keep it in dev.
        const path = import.meta.env.PROD ? url.replace(/^\/v1/, "") : url;
        const token = useAuthStore.getState().accessToken;
        const headers = new Headers();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const res = await fetch(`${apiBase}${path}`, { headers });
        if (!res.ok) {
          setError(`Preview failed: HTTP ${res.status}`);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  if (error) return <div style={{ padding: 20, fontSize: 12, color: "var(--rose)" }}>{error}</div>;
  if (!src) return <div style={{ padding: 40, fontSize: 12, color: "var(--ink-3)" }}>Loading preview…</div>;
  // Wrap in a button so mobile users can tap to open full-size. The CSS
  // class handles sizing per breakpoint — .inbox-preview-image is a small
  // thumbnail on phones, a large sticky preview on desktop.
  return (
    <button
      type="button"
      onClick={onOpen}
      className="inbox-preview-image-btn"
      title="Tap to open the full image"
    >
      <img src={src} alt={alt} className="inbox-preview-image" />
    </button>
  );
}
